import React, { useMemo, useState } from 'react';

import { Link } from '~/lib/react-router-dom';
import dayjs from '~/timeInit';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';

import { AuditEventModelType, useGetAuditEventsByModel } from '~/hooks/apis/audit/useGetAuditEventsByModel';

import { AuditLog } from '~/types/responses';

type AuditEventSummaryProps = {
  title?: string;
  modelType: AuditEventModelType;
  modelId?: string | null;
  serviceName?: string;
  limit?: number;
  timeFrameMonths?: number;
};

function buildAuditLogUrl({
  modelId,
  modelType,
  serviceName,
  timeFrameMonths,
}: {
  modelId: string;
  modelType: string;
  serviceName: string;
  timeFrameMonths: number;
}) {
  const params = new URLSearchParams({
    filterType: 'modelAndId',
    model: modelType,
    modelId,
    service: serviceName,
    timeFrame: String(timeFrameMonths),
  });

  return `/system/audit-logs?${params.toString()}`;
}

function getChangedFields(log: AuditLog): Array<string> {
  const diff = log.model?.diff;
  const fieldNames = new Set<string>();

  Object.keys(diff?.added ?? {}).forEach((field) => fieldNames.add(field));
  Object.keys(diff?.updated ?? {}).forEach((field) => fieldNames.add(field));
  (diff?.deleted ?? []).forEach((field) => fieldNames.add(field));

  return Array.from(fieldNames);
}

function getActorLabel(log: AuditLog) {
  return log.actor.email || log.actor.id || 'Unknown actor';
}

function getActionLabel(action: string) {
  if (!action) {
    return 'Changed';
  }

  return action.charAt(0).toUpperCase() + action.slice(1);
}

export default function AuditEventSummary({
  title = 'Recent audit events',
  modelType,
  modelId,
  serviceName = 'svc-promo',
  limit = 20,
  timeFrameMonths = 24,
}: AuditEventSummaryProps) {
  const [expanded, setExpanded] = useState(false);
  const { data, isError, isFetching, error, normalizedModelId } = useGetAuditEventsByModel({
    serviceName,
    modelType,
    modelId,
    limit,
    timeFrameMonths,
  });
  const auditLogUrl = useMemo(
    () =>
      normalizedModelId
        ? buildAuditLogUrl({
            modelId: normalizedModelId,
            modelType,
            serviceName,
            timeFrameMonths,
          })
        : '/system/audit-logs',
    [modelType, normalizedModelId, serviceName, timeFrameMonths],
  );

  const logs = data?.result ?? [];
  const total = data?.pagination?.total_items ?? logs.length;
  const latestLog = logs[0];
  const showRecentUpdate =
    latestLog && dayjs().diff(dayjs(latestLog.timestamp), 'day', true) <= 7
      ? dayjs(latestLog.timestamp).fromNow()
      : null;

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Accordion expanded={expanded} onChange={(_, nextExpanded) => setExpanded(nextExpanded)} disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            <HistoryIcon fontSize="small" color="action" />
            <Typography variant="subtitle2">{title}</Typography>
            {showRecentUpdate ? <Chip size="small" label={`updated ${showRecentUpdate}`} /> : null}
            {total > 0 ? <Chip size="small" label={`${total} audit events`} /> : null}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1.5}>
            <Stack direction="row" justifyContent="flex-end">
              <Button
                component={Link}
                to={auditLogUrl}
                variant="text"
                size="small"
                startIcon={<OpenInNewIcon fontSize="small" />}
              >
                Open audit logs
              </Button>
            </Stack>

            {isFetching ? <LinearProgress /> : null}

            {isError ? (
              <Alert severity="warning">
                Could not load audit events
                {error instanceof Error ? `: ${error.message}` : ''}
              </Alert>
            ) : null}

            {!isFetching && !isError && logs.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No audit events found for this {modelType}.
              </Typography>
            ) : null}

            {logs.length > 0 ? (
              <Stack divider={<Divider flexItem />} spacing={1}>
                {logs.map((log) => {
                  const changedFields = getChangedFields(log);

                  return (
                    <Stack key={log.id} spacing={0.5}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                        <Chip size="small" label={getActionLabel(log.action)} variant="outlined" />
                        <Typography variant="body2" fontWeight={500}>
                          {getActorLabel(log)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {dayjs(log.timestamp).format('D MMM YYYY, h:mm a')}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {changedFields.length > 0
                          ? `Changed ${changedFields.slice(0, 6).join(', ')}${
                              changedFields.length > 6 ? ` and ${changedFields.length - 6} more` : ''
                            }`
                          : log.resource}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            ) : null}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
